import { useMemo } from "react";

import { useWriterComponentState } from "./useWriterComponentState";

import type { UpdateNotificationsBody } from "../components/Notifcation/components/NotificationModal/types";

interface Props {
  onUpdateSuccess?: () => void;
  onUpdateFailure?: (error: Error) => void;
}

export function useUpdateNotifications(props: Props = {}) {
  const { onUpdateSuccess, onUpdateFailure } = props;

  const writeConfig = useMemo(
    () => ({
      method: "patch" as const,
      url: "/notifications/update_many",
    }),
    [],
  );

  const writerState = useWriterComponentState({
    writeConfig,
    errorMessage: "Failed to update notifications",
    onWriteFailure: (error) => {
      if (onUpdateFailure) onUpdateFailure(error);
    },
  });

  const updateNotifications = (body: UpdateNotificationsBody) => {
    writerState.write(() => {
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
    }, body);
  };

  const markNotificationsAsRead = (notificationIds: number[]) => {
    if (notificationIds.length === 0) {
      return;
    }

    updateNotifications({
      notifications: notificationIds.map((id) => ({
        id,
        read: true,
        delete: false,
      })),
    });
  };

  const markNotificationsAsDeleted = (notificationIds: number[]) => {
    if (notificationIds.length === 0) {
      return;
    }

    updateNotifications({
      notifications: notificationIds.map((id) => ({
        id,
        read: false,
        delete: true,
      })),
    });
  };

  return {
    markNotificationsAsRead,
    markNotificationsAsDeleted,
    loading: writerState.loading,
    error: writerState.error,
    success: writerState.success,
    resetState: writerState.resetState,
  };
}
